'use client'

import { NavigationQueryResult } from '@repo/sanity/types'
import { cn } from '@repo/ui/lib/utils'
import { motion } from 'motion/react'
import { FunctionComponent, useMemo } from 'react'
import { useNavigation } from '../../../providers/NavigationProvider'
import { PageDto } from '../../../types/page.types'
import CartSheet from '../cart/CartSheet'
import SearchSheet from '../search/SearchSheet'
import WishlistSheet from '../wishlist/WishlistSheet'
import AnnouncementButton from './AnnouncementButton'
import MobileMenuSheet from './MobileMenuSheet'
import NavigationLinkLoop from './NavigationLinkLoop'
import NavigationLogo from './NavigationLogo'

interface Props {
  pageDto: PageDto
  navigation: NavigationQueryResult
  className?: string
}

const Navigation: FunctionComponent<Props> = ({ pageDto, navigation }) => {
  const { hidden, shouldUseInverse, shouldHideOnPage } = useNavigation()
  const useInverse = useMemo(() => shouldUseInverse(navigation), [navigation, shouldUseInverse])
  const hideOnPage = useMemo(() => shouldHideOnPage(navigation), [navigation, shouldHideOnPage])

  if (hideOnPage) {
    return null
  }

  return (
    <motion.nav
      className={cn(
        'top-miista-navigation-margin-y h-miista-navigation-height pl-miista-page-padding-content pr-miista-page-padding fixed inset-x-0 z-50 flex w-full items-center justify-between gap-4',
        useInverse ? 'text-white' : 'text-black',
      )}
      variants={{
        visible: { transform: 'translate3d(0, 0, 0)', pointerEvents: 'auto' },
        hidden: { transform: 'translate3d(0, calc((100% + var(--spacing-miista-navigation-margin-y)) * -1), 0)', pointerEvents: 'none' },
      }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      animate={hidden ? 'hidden' : 'visible'}
      initial='visible'
    >
      <NavigationLogo pageDto={pageDto} />
      <ul className='ml-0 mr-auto hidden items-center lg:flex'>
        <NavigationLinkLoop locale={pageDto.params.locale} navigation={navigation} links={navigation?.primaryLinks || []} />
        <AnnouncementButton locale={pageDto.params.locale} navigation={navigation} />
        <SearchSheet pageDto={pageDto} navigation={navigation} />
      </ul>
      <ul className='flex items-center text-right'>
        <AnnouncementButton className='lg:hidden' locale={pageDto.params.locale} navigation={navigation} />
        <NavigationLinkLoop className='hidden lg:inline-flex' locale={pageDto.params.locale} navigation={navigation} links={navigation?.secondaryLinks || []} />
        <MobileMenuSheet className='lg:hidden' pageDto={pageDto} navigation={navigation} />
        <WishlistSheet pageDto={pageDto} navigation={navigation} />
        <CartSheet pageDto={pageDto} navigation={navigation} />
      </ul>
    </motion.nav>
  )
}

export default Navigation
